import { Link, useNavigate, NavLink } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, LogIn, Menu, X, User2, Building2 } from "lucide-react";
import { useState } from "react";
import { CurrencyToggle } from "@/components/CurrencyToggle";

export function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const links = [
    { to: "/marketplace", label: "Marketplace" },
    { to: "/secondary", label: "Secondary" },
    { to: "/calculator", label: "Calculator" },
    { to: "/how-it-works", label: "How It Works" },
    { to: "/faq", label: "FAQ" },
  ];
  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#FAF8F5]/85 border-b border-[#E5DFD5]" data-testid="site-navbar">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="brand-link">
          <div className="h-9 w-9 rounded-md bg-[#0B192C] flex items-center justify-center">
            <Building2 className="h-5 w-5 text-[#C5A059]" />
          </div>
          <div>
            <div className="font-serif text-xl leading-none">OwnNest</div>
            <div className="overline mt-1">Fractional Estate</div>
          </div>
        </Link>
        <nav className="hidden lg:flex items-center gap-9 text-[15px]">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) => `gold-underline ${isActive ? "text-[#0B192C]" : "text-slate-600 hover:text-[#0B192C]"}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-2">
          <CurrencyToggle />
          {user && user.user_id ? (
            <>
              <Button variant="ghost" onClick={() => navigate(user.role === "admin" ? "/admin" : "/portfolio")} data-testid="nav-dashboard-btn">
                <LayoutDashboard className="h-4 w-4 mr-2" />
                {user.role === "admin" ? "Admin" : "Portfolio"}
              </Button>
              <Button variant="outline" onClick={logout} data-testid="nav-logout-btn">Sign out</Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => navigate("/login")} data-testid="nav-login-btn">
                <LogIn className="h-4 w-4 mr-2" />Sign in
              </Button>
              <Button className="btn-gold rounded-full px-5" onClick={() => navigate("/register")} data-testid="nav-register-btn">
                Start Investing
              </Button>
            </>
          )}
        </div>
        <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden border-t border-[#E5DFD5] px-6 py-4 space-y-3 bg-white">
          <div className="pb-2 border-b border-[#E5DFD5] mb-2"><CurrencyToggle /></div>
          {links.map((l) => (
            <Link key={l.to} to={l.to} className="block py-1" onClick={() => setOpen(false)}>{l.label}</Link>
          ))}
          {user && user.user_id ? (
            <>
              <Link to={user.role === "admin" ? "/admin" : "/portfolio"} className="block py-1" onClick={() => setOpen(false)}>Dashboard</Link>
              <button onClick={() => { logout(); setOpen(false); }} className="block py-1 text-left">Sign out</button>
            </>
          ) : (
            <>
              <Link to="/login" className="block py-1" onClick={() => setOpen(false)}>Sign in</Link>
              <Link to="/register" className="block py-1 text-[#0B192C] font-medium" onClick={() => setOpen(false)}>Start Investing</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-[#E5DFD5] bg-[#0B192C] text-[#F3EFEA] mt-16">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid md:grid-cols-4 gap-8">
        <div>
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-md bg-white/5 border border-white/10 flex items-center justify-center">
              <Building2 className="h-5 w-5 text-[#C5A059]" />
            </div>
            <span className="font-serif text-xl">OwnNest</span>
          </div>
          <p className="mt-4 text-sm text-white/70 leading-relaxed">Institutional-grade fractional ownership of income-producing real estate — from $1,000.</p>
        </div>
        <div>
          <div className="overline text-[#C5A059]">Platform</div>
          <ul className="mt-4 space-y-2 text-sm">
            <li><Link to="/marketplace" className="hover:text-[#C5A059]">Marketplace</Link></li>
            <li><Link to="/calculator" className="hover:text-[#C5A059]">Calculator</Link></li>
            <li><Link to="/how-it-works" className="hover:text-[#C5A059]">How it works</Link></li>
          </ul>
        </div>
        <div>
          <div className="overline text-[#C5A059]">Company</div>
          <ul className="mt-4 space-y-2 text-sm">
            <li><Link to="/about" className="hover:text-[#C5A059]">About</Link></li>
            <li><Link to="/contact" className="hover:text-[#C5A059]">Contact</Link></li>
            <li><Link to="/faq" className="hover:text-[#C5A059]">FAQ</Link></li>
          </ul>
        </div>
        <div>
          <div className="overline text-[#C5A059]">Legal</div>
          <ul className="mt-4 space-y-2 text-sm">
            <li><Link to="/terms" className="hover:text-[#C5A059]">Terms</Link></li>
            <li><Link to="/privacy" className="hover:text-[#C5A059]">Privacy</Link></li>
            <li><Link to="/risk" className="hover:text-[#C5A059]">Risk Disclosure</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 text-xs text-white/60 flex flex-col md:flex-row justify-between gap-2">
          <div>© {new Date().getFullYear()} OwnNest Capital. All rights reserved.</div>
          <div>Investments involve risk. Past performance is not indicative of future results.</div>
        </div>
      </div>
    </footer>
  );
}

export function PublicLayout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-[#FAF8F5]">
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
